import { api } from '@/api';
import { httpClient } from '@/httpClient';
import { numBool } from '@/helpers/numBool';
import { ENTITY_STATUS, sortByStatus, withStatus } from '@/helpers/entityStatus';

export default {
  namespaced: true,
  state: {
    items: [],
    tags: [],
    tagCategories: [],
    isLoading: false,

    current: {
      isLoading: false,
    },
    currentTopViewUrl: null,
    designFiles: [],
    deprecatedItems: [],
    deletedItems: [],
    objects: [],
  },
  mutations: {
    setFurniture(state, items) {
      state.items = items;
    },
    setFurnitureTags(state, tags) {
      state.tags = tags;
    },
    setTagCategories(state, tagCategories) {
      state.tagCategories = tagCategories;
    },
    setLoading(state, loading) {
      state.isLoading = loading;
    },
    setCurrentFurniture(state, data) {
      state.current = {
        ...state.current,
        ...data,
      };
    },
    setCurrentLoading(state, loading) {
      state.current.isLoading = loading;
    },
    setDesignFiles(state, designFiles) {
      state.designFiles = Array.isArray(designFiles)
        ? designFiles
        : designFiles?.items ?? designFiles?.data ?? [];
    },
    setDeprecatedFurniture(state, items) {
      state.deprecatedItems = items;
    },
    setDeletedFurniture(state, deletedItems) {
      state.deletedItems = deletedItems;
    },
    setFurnitureObjects(state, objects) {
      state.objects = objects;
    },
  },
  actions: {
    getDesignFiles(store) {
      store.commit('setLoading', true);

      return api.getDesignFiles().then(({ data }) => {
        store.commit('setDesignFiles', data);
        store.commit('setLoading', false);
      });
    },

    getList(store) {
      store.commit('setLoading', true);

      const params = { page: store.state.page, deprecated: false };

      return api.getFurnitureList(params).then(({ data }) => {
        store.commit('setFurniture', data);
        store.commit('setLoading', false);
      });
    },

    getDeprecatedFurnitureList({ commit, state }) {
      commit('setLoading', true);
      const params = {
        page: state.page,
        deprecated: true,
      };
      return api.getFurnitureList(params)
        .then(({ data }) => {
          commit('setDeprecatedFurniture', data);
        })
        .finally(() => commit('setLoading', false));
    },

    async getFurniture({ commit }, id) {
      commit('setCurrentLoading', true);
      return httpClient.get(`api/furniture/${id}`).then(({ data }) => {
        commit('setCurrentFurniture', data);
        commit('setCurrentLoading', false);
      });
    },

    async getFurnitureTags({ commit }) {
      const { data } = await api.getFurnitureTags();
      commit('setFurnitureTags', data);
      return data;
    },

    async getTagCategories({ commit }, params = { entity_type: 'furniture' }) {
      const { data } = await api.getTagCategories(params);
      commit('setTagCategories', data);
      return data;
    },

    async invalidateFurniture() {
      return api.invalidateFurniture().then((resp) => resp);
    },

    updateFurnitureParams({ commit }, { id, params }) {
      const fd = new FormData();

      fd.append('x_length', params.xLength);
      fd.append('y_length', params.yLength);
      fd.append('z_length', params.zLength);
      fd.append('z_location', params.zLocation);
      fd.append('is_scalable', numBool(params.isScalable));
      fd.append('is_procedural', numBool(params.isProcedural));
      fd.append('spatial_anchor', params.spatialAnchor);
      fd.append('type', params.type);

      params.styles.forEach((style) => fd.append('styles[]', style));
      params.roomTypes.forEach((type) => fd.append('room_types[]', type));
      (params.tagIds || []).forEach((tagId) => fd.append('tag_ids[]', tagId));

      if (params.valueMesh) {
        fd.append('mesh', params.valueMesh);
      }

      return httpClient.post(`/api/furniture/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(({ data }) => {
        commit('setCurrentFurniture', data);
        return data;
      });
    },

    async createFurnitureTag({ dispatch }, payload) {
      await api.createFurnitureTag(payload);
      return dispatch('getFurnitureTags');
    },

    async updateFurnitureTag({ dispatch }, { id, payload }) {
      await api.updateFurnitureTag(id, payload);
      return dispatch('getFurnitureTags');
    },

    async deleteFurnitureTag({ dispatch }, id) {
      await api.deleteFurnitureTag(id);
      return dispatch('getFurnitureTags');
    },

    async createTagCategory({ dispatch }, payload) {
      await api.createTagCategory(payload);
      return dispatch('getTagCategories');
    },

    async updateTagCategory({ dispatch }, { id, payload }) {
      await api.updateTagCategory(id, payload);
      return dispatch('getTagCategories');
    },

    async deleteTagCategory({ dispatch }, id) {
      await api.deleteTagCategory(id);
      return dispatch('getTagCategories');
    },

    renameMaterial({ commit }, { id, params }) {
      return httpClient.post(`api/furniture/${id}/textures/rename_material`, params).then(({ data }) => {
        commit('setCurrentFurniture', data);
      });
    },

    async updateFurnitureImg({ commit, state }, { id, img, type }) {
      const fd = new FormData();
      fd.append('image', img);
      fd.append('type', type);
      const response = await httpClient.post(`/api/furniture/${id}/update_image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      commit('setCurrentFurniture', response.data);

      commit('setFurniture', state.items.map((item) => {
        if (item.id === response.data.id) {
          return response.data;
        }

        return item;
      }));

      return true;
    },

    deleteFurnitureTopView(store, id) {
      return api.deleteFurnitureTopView(id).then((resp) => resp);
    },

    deleteFurniturePreview(store, id) {
      return api.deleteFurniturePreview(id).then((resp) => resp);
    },

    deleteFurnitureById(store, id) {
      return api.deleteFurniture(id);
    },

    createDesignFile(store, {
      file, name,
    }) {
      const fd = new FormData();
      fd.append('file_name', name);
      fd.append('file', file);
      return api.createDesignFile(fd).then(() => {
        store.dispatch('getDesignFiles');
      });
    },

    deleteDesignFile(store, id) {
      return api.deleteDesignFile(id).then(() => {
        store.dispatch('getDesignFiles');
      });
    },

    changeDesignFile(store, {
      id, file, name,
    }) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('file_name', name);
      return api.changeDesignFile(id, fd).then(() => {
        store.dispatch('getDesignFiles');
      });
    },

    downloadDesignFile(store, id) {
      return api.downloadDesignFile(id);
    },

    getDeletedFurnitureList(store) {
      store.commit('setLoading', true);
      return api.getDeletedFurnitureList().then(({ data }) => {
        store.commit('setDeletedFurniture', data);
        store.commit('setLoading', false);
      });
    },

    restoreFurniture(store, id) {
      return api.restoreFurniture(id).then((resp) => {
        store.dispatch('getDeletedFurnitureList');
        return resp;
      });
    },

    // Один список на все состояния. /api/furniture отдаёт каждое состояние
    // своим запросом: archived=true возвращает только корзину и снимает
    // фильтр по deprecated, поэтому три запроса не пересекаются.
    async getFurnitureObjects({ commit }) {
      commit('setLoading', true);

      try {
        const [actual, deprecated, archived] = await Promise.all([
          api.getFurnitureList({ deprecated: false }),
          api.getFurnitureList({ deprecated: true }),
          api.getFurnitureList({ archived: true }),
        ]);

        commit('setFurnitureObjects', sortByStatus([
          ...withStatus(actual.data, ENTITY_STATUS.ACTUAL),
          ...withStatus(deprecated.data, ENTITY_STATUS.DEPRECATED),
          ...withStatus(archived.data, ENTITY_STATUS.ARCHIVED),
        ]));
      } finally {
        commit('setLoading', false);
      }
    },
  },
};
